import { useCallback, useEffect, useState } from 'react'
import { ExternalLink } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import api from '@/services/api'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { mapDealFromApi } from '@/adapters/deals'
import { getFriendlyError } from '@/utils/getFriendlyError'
import { dealsPagePathForDeal } from '@/utils/dealsPageNavigation'
import type { Deal } from '@/types'
import { DealProposalTab } from '@/components/Deals/DealDetailPanel/DealProposalTab'

type Props = {
  open: boolean
  dealId: string | null
  onClose: () => void
}

export function ConvertProposalElaborationModal({ open, dealId, onClose }: Props) {
  const navigate = useNavigate()
  const [deal, setDeal] = useState<Deal | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadDeal = useCallback(async (id: string) => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await api.get(`/deals/${id}`)
      setDeal(mapDealFromApi(data.data ?? data))
    } catch (err: unknown) {
      setError(getFriendlyError(err, 'Erro ao carregar negociação'))
      setDeal(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!open || !dealId) {
      setDeal(null)
      setError(null)
      return
    }
    void loadDeal(dealId)
  }, [open, dealId, loadDeal])

  const handleOpenInDeals = () => {
    if (!dealId) return
    onClose()
    navigate(dealsPagePathForDeal(dealId))
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title='Elaborar proposta'
      description='Monte o plano até a reunião de apresentação.'
      size='3xl'
      stackPriority='high'
      footer={
        <div className='flex flex-wrap justify-end gap-2'>
          <Button variant='ghost' size='sm' onClick={onClose}>
            Fechar
          </Button>
          <Button
            variant='secondary'
            size='sm'
            icon={<ExternalLink size={14} />}
            onClick={handleOpenInDeals}
            disabled={!dealId}
          >
            Abrir em Negociações
          </Button>
        </div>
      }
    >
      {loading ? (
        <p className='py-8 text-center text-sm text-text-muted'>Carregando negociação…</p>
      ) : error ? (
        <div className='space-y-3 py-4 text-center'>
          <p className='text-sm text-red-400'>{error}</p>
          {dealId ? (
            <Button variant='secondary' size='sm' onClick={() => void loadDeal(dealId)}>
              Tentar novamente
            </Button>
          ) : null}
        </div>
      ) : deal ? (
        <DealProposalTab
          deal={deal}
          onDealPatched={(updates) => setDeal((current) => (current ? { ...current, ...updates } : current))}
        />
      ) : null}
    </Modal>
  )
}

export default ConvertProposalElaborationModal
